<template>
  <header 
    class="app-header bg-white"
    :style="headerStyles"
  >
    <v-container
      fluid
      class="pa-0"
      :class="{
        'settings-header': $route?.meta.navLayout === 'settings'
      }"
    >
      <!-- Cover Image Start -->
      <aside
        class="cover-image bg-primary-lighten-5"
        :style="{
          '--bg-img': `url(${$auth.user?.cover_picture_id ? '/api/v1/' + $file.url($auth.user.cover_picture_id, 'large') : ''})`
        }"
        :class="{
          'cover-image-no-bg': !$auth.user?.cover_picture_id
        }"
        v-if="$vuetify.display.mdAndUp"
      >
        <div 
          class="cover-image__corner bg-primary-lighten-5"
        />
      </aside>
      <!-- Cover Image End -->
      <!-- Navbar Start -->
      <v-row 
        class="ga-2 ga-md-4 px-4 py-1 my-0 main-nav"
      >
        <v-col
          class="d-flex justify-start align-center flex-grow-0 pr-0 py-0"
        >       
          <AppNav 
            v-if="$vuetify.display.smAndDown" 
          />
        </v-col>
  
        <v-col
          class="d-flex justify-center align-center flex-grow-1 pa-0"
        >
          <CalendarNav 
            v-if="[ROUTES.CALENDAR, ROUTES.CALENDAR_ANNOTATIONS_ADD, ROUTES.ANNOTATIONS_ADD].includes($route?.name as string)"
            @filter="showFilters = !showFilters"
          />
          <template v-else>
            <v-btn
              flat
              :to="backOrCalendar"
            >
              <v-icon
                size="20"
              >
                arrow_back
              </v-icon>
              <span v-if="!pageTitle">
                Back
              </span>
            </v-btn>
            <AppHeading
              :class="{
                'line-clamp-1': $vuetify.display.smAndDown,
              }"
              :level="5"
            >
              {{ pageTitle }}
            </AppHeading>
          </template>
        </v-col>
  
        <v-col
          class="d-flex justify-end align-center flex-grow-0 pl-0 py-0"
        >
          <AccountNav 
            :scroll-progress="scrollProgress"     
          />
        </v-col>
      </v-row>
      <!-- Navbar End -->
    </v-container>
    
    <!-- Calendar Filter -->
    <CalendarFilter 
      v-model="showFilters"
      v-if="[ROUTES.CALENDAR, ROUTES.CALENDAR_ANNOTATIONS_ADD, ROUTES.ANNOTATIONS_ADD].includes($route?.name as string)"
    />
  </header>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {capitalize} from 'lodash'
import * as Navs from './Navs'
import CalendarFilter from '@/components/CalendarFilter.vue'
import AppHeading from '@/components/AppHeading.vue'

export default defineComponent({
  name: 'AppHeader',
  components: {
    CalendarNav: Navs.CalendarNav,
    AccountNav: Navs.AccountNav,
    AppNav: Navs.AppNav,
    CalendarFilter,
    AppHeading
  },
  data() {
    return {
      scrollProgress: 0,
      scrollThreshold: 100,
      showFilters: false,
    }
  },
  mounted() {
    // Shrink the header when scrolling down
    this.updateScrollProgress()
    window.addEventListener("scroll", this.updateScrollProgress)
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.updateScrollProgress)
  },
  computed: {
    headerStyles() {
      const top = 0 - 192 * this.scrollProgress // from 0px to -192px
      const boxShadow =
        this.scrollProgress > 0 && this.$route.name !== this.ROUTES.SETTINGS
          ? `0.25rem 0.25rem 1rem 0 rgba(0, 0, 0, ${0.13 * this.scrollProgress})`
          : "none"
      return {
        top: this.$vuetify.display.mdAndUp ? `calc(${top}px - var(--v-safe-area-top))` : "0px",
        boxShadow,
      }
    },
    pageTitle() {
      if (!this.$route?.meta?.title) return ''
      if (this.$vuetify.display.smAndDown && this.$route?.meta?.shortTitle) {
        return this.$route?.meta?.shortTitle
      }
      return this.$route?.meta?.title || capitalize(this.$route?.name as string).replace('-', ' ')
    },
    backOrCalendar() {
      if (this.$route.name === this.ROUTES.TODOS_EDIT) {
        return this.$routeTo(this.ROUTES.TODOS)
      }
      return this.$routeTo(this.ROUTES.CALENDAR)
    }
  },
  methods: {
    capitalize,
    updateScrollProgress() {

      //if we have v-overlay--active, we don't want to show the header
      if (document.querySelector('.v-overlay--active')) {
        this.scrollProgress = 1
        return
      }
      
      this.scrollProgress = Math.min(1, window.scrollY / this.scrollThreshold)
    }
  }
})

</script>

<style lang="scss" scoped>
.app-header {
  background-color: var(--v-shade-white);
  position: sticky;
  top: 0;
  // Pull back out of the wrap's safe area padding and re-apply it inside, so the
  // header background covers the cutout strip once it sticks instead of letting
  // the list scroll through it. Net height is unchanged.
  margin-top: calc(-1 * var(--v-safe-area-top));
  padding-top: var(--v-safe-area-top);
  width: 100%;
  z-index: 100;
  overflow-x: hidden;

  @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
    margin-left: 6rem;
    width: calc(100% - 6rem);
  }
}

.main-nav {
  min-height: map-get(vars.$headerHeight, 'sm');
  @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
    min-height: map-get(vars.$headerHeight, 'md');
  }
}

.cover-image {
  background: var(--bg-img) no-repeat center center;
  background-size: cover;
  border-radius: 2rem 0 2rem 0;
  min-height: 12rem;
  position: relative;

  &.cover-image-no-bg {
    background: url('@/../img/window2.png') no-repeat center center;
  }

  .cover-image__corner {
    width: 3rem;
    height: 3rem;
    top: 0;
    left: 0;
    position: absolute;
    z-index: -1;
  }
}

</style>